import React, { useMemo, useState, useContext } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Paper,
  Divider,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/Email";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { UserDataContext } from "../components/UserDataContext";
import VN from "../components/VN";

const faqCategories = [
  {
    id: "onas",
    title: "O nas",
    items: [
      [
        "Dlaczego powstała ta strona?",
        "**twojaopiekunka.pl** powstała z potrzeby stworzenia bezpiecznego i przejrzystego miejsca, w którym można znaleźć opiekunkę dla osoby starszej lub wymagającej wsparcia. \n Wiemy, że powierzenie opieki nad bliską osobą komuś obcemu jest decyzją, której często towarzyszy niepewność i wiele pytań: \n\n* Kim jest ta osoba?\n\n* Jakie ma doświadczenie? \n\n*Czy posiada odpowiednie kwalifikacje?\n\n* Czy można jej zaufać?\n\n Dlatego stworzyliśmy portal, który nie ogranicza się do publikowania ogłoszeń. Chcemy, aby osoby poszukujące opieki mogły podejmować decyzję na podstawie możliwie rzetelnych informacji o opiekunce. \n\nNaszym wyróżnikiem jest weryfikacja opiekunek, obejmująca – w zależności od zakresu weryfikacji – m.in. potwierdzenie tożsamości, doświadczenia, kwalifikacji, referencji oraz ocenę wybranych umiejętności przez uprawnioną osobę. \n\nZ drugiej strony chcemy stworzyć dobre miejsce również dla samych opiekunek – osób, które swoją pracą i doświadczeniem zasługują na wiarygodność, szacunek i możliwość pokazania swoich kompetencji.\n\n **twojaopiekunka.pl** ma łączyć te dwie potrzeby: \n\n*bezpieczeństwo rodziny \n\n* uczciwe przedstawienie kompetencji opiekunki. \n\nNie obiecujemy, że wyeliminujemy każde ryzyko. Chcemy natomiast zrobić wszystko, co rozsądne i możliwe, aby zmniejszyć niepewność przed podjęciem decyzji o powierzeniu komuś opieki nad bliską osobą.",
      ],
    ],
  },
  {
    id: "szukajacy",
    title: "Dla osób szukających opiekunki",
    items: [
      [
        "Jak znaleźć opiekunkę na twojaopiekunka.pl?",
        "Po utworzeniu konta w systemie jako **''Szukam opiekunki''** wybierz interesujące Cię kryteria, takie jak miejsce, doświadczenie, zakres opieki czy dostępność. Następnie możesz przeglądać profile opiekunek, porównywać informacje oraz sprawdzać zakres przeprowadzonej weryfikacji.",
      ],
      [
        "Czy każda opiekunka na portalu jest zweryfikowana?",
        "**Nie**.\n\n Na portalu mogą znajdować się zarówno opiekunki będące w trakcie weryfikacji, jak i takie, które przeszły określone etapy procesu. Zakres przeprowadzonej weryfikacji jest wskazywany przy profilu.",
      ],
      [
        "Co oznacza status „Zweryfikowana Opiekunka”?",
        "Oznacza, że opiekunka pozytywnie przeszła określony zakres procedury weryfikacyjnej. W zależności od poziomu weryfikacja może obejmować m.in. potwierdzenie tożsamości, dokumentów, doświadczenia, referencji oraz sprawdzenie określonych umiejętności.",
      ],
      [
        "Czy „Zweryfikowana Opiekunka” oznacza gwarancję jakości?",
        "**Nie**.\n\n Status potwierdza przejście określonego procesu weryfikacyjnego i nie jest gwarancją przyszłego sposobu wykonywania usług. Przed rozpoczęciem współpracy warto przeprowadzić własną rozmowę i ustalić wszystkie warunki.",
      ],
      [
        "Czy mogę zobaczyć dokumenty opiekunki?",
        "Nie wszystkie dokumenty są publicznie dostępne. Portal może potwierdzać ich weryfikację poprzez odpowiedni status, bez ujawniania danych osobowych lub dokumentów zawierających informacje poufne.",
      ],
      [
        "Czy mogę skontaktować się z opiekunką przed podjęciem decyzji?",
        "**Tak**. Portal umożliwia kontakt z opiekunką za pomocą funkcji komunikacyjnych dostępnych dla użytkowników.",
      ],
      [
        "Czy mogę wystawić opinię opiekunce?",
        "**Tak**, jeżeli funkcja opinii jest dostępna dla Twojego konta. Opinia powinna opierać się na rzeczywistym doświadczeniu ze współpracy.",
      ],
      [
        "Co zrobić w sytuacji zagrożenia życia lub zdrowia podopiecznego?",
        "W sytuacji bezpośredniego zagrożenia należy przede wszystkim skontaktować się z odpowiednimi służbami. Portal nie zastępuje pogotowia, policji ani innych służb ratunkowych.",
      ],
    ],
  },
  {
    id: "opiekunki",
    title: "Dla opiekunek",
    items: [
      [
        "Jak zostać opiekunką na twojaopiekunka.pl?",
        "Załóż konto, uzupełnij profil i przedstaw informacje dotyczące doświadczenia, kwalifikacji, dyspozycyjności oraz zakresu oferowanej opieki. Następnie możesz przejść dostępne etapy weryfikacji.",
      ],
      [
        "Czy rejestracja jako opiekunka jest płatna?",
        "Zależy to od aktualnego cennika portalu. Poszczególne funkcje lub poziomy weryfikacji mogą być oferowane na różnych zasadach.",
      ],
      [
        "Jak wygląda weryfikacja opiekunki?",
        "Proces może obejmować potwierdzenie tożsamości, sprawdzenie dokumentów, doświadczenia i referencji oraz ocenę wybranych umiejętności praktycznych lub teoretycznych.",
      ],
      [
        "Kto sprawdza moje umiejętności?",
        "Ocena może być przeprowadzana przez osobę posiadającą odpowiednie kwalifikacje, np. wykwalifikowaną pielęgniarkę. Zakres oceny zależy od rodzaju weryfikacji.",
      ],
      [
        "Jakie umiejętności mogą być sprawdzane?",
        "W zależności od programu mogą być oceniane m.in. podstawowe czynności opiekuńcze, zasady bezpieczeństwa, pomoc osobie niesamodzielnej, higiena oraz prawidłowe reagowanie w określonych sytuacjach.",
      ],
      [
        "Co otrzymuję po pozytywnym przejściu weryfikacji?",
        "Twój profil może otrzymać odpowiedni status potwierdzający przejście danego etapu. Dzięki temu osoby szukające opiekunki mogą łatwiej ocenić zakres przeprowadzonej weryfikacji.",
      ],
      [
        "Czy status „Zweryfikowana Opiekunka” jest dożywotni?",
        "Nie musi być. Niektóre elementy weryfikacji mogą wymagać okresowego odnowienia lub aktualizacji.",
      ],
      [
        "Co jeśli nie przejdę weryfikacji?",
        "Otrzymasz informację o wyniku w zakresie przewidzianym dla danego etapu. W określonych sytuacjach możesz uzupełnić dokumenty, wyjaśnić rozbieżności lub ponownie przystąpić do wybranego etapu.",
      ],
    ],
  },
  {
    id: "kontrole",
    title: "Weryfikacja i kontrole",
    items: [
      [
        "Dlaczego opiekunki mogą być kontrolowane?",
        "Kontrole mają służyć zwiększeniu bezpieczeństwa oraz sprawdzeniu, czy określone informacje przedstawione w profilu odpowiadają rzeczywistości i czy uzgodnione obowiązki są wykonywane prawidłowo.",
      ],
      [
        "Czy kontrola może być niezapowiedziana?",
        "W ramach programu jakości może być przewidziana kontrola niezapowiedziana, ale jej przeprowadzenie musi odbywać się zgodnie z prawem oraz warunkami, na które odpowiednie osoby skutecznie się zgodziły.",
      ],
      [
        "Czy kontroler może wejść do mojego domu bez pozwolenia?",
        "Nie. Sam fakt korzystania z portalu nie daje Operatorowi prawa do swobodnego wejścia do prywatnego mieszkania. Kontrola w miejscu prywatnym musi mieć odpowiednią podstawę prawną, zgodę albo inną skuteczną podstawę uprawniającą do jej przeprowadzenia.",
      ],
      [
        "Co sprawdza kontroler?",
        "Zakres kontroli może obejmować m.in. sposób wykonywania uzgodnionych czynności, przestrzeganie zasad bezpieczeństwa oraz zgodność określonych informacji znajdujących się w profilu ze stanem faktycznym.",
      ],
      [
        "Co się dzieje, jeśli kontrola wykryje nieprawidłowości?",
        "W zależności od rodzaju i wagi nieprawidłowości możliwe jest wezwanie do ich usunięcia, ponowna weryfikacja, czasowe zawieszenie statusu, ograniczenie widoczności profilu albo zawieszenie konta.",
      ],
      [
        "Czy mogę odwołać się od wyniku kontroli?",
        "Tak. Opiekunka może złożyć odwołanie zgodnie z procedurą określoną w Regulaminie Weryfikacji i Kontroli.",
      ],
    ],
  },
  {
    id: "bezpieczenstwo",
    title: "Bezpieczeństwo i prywatność",
    items: [
      [
        "Czy moje dane są bezpieczne?",
        "Portal stosuje rozwiązania techniczne i organizacyjne mające chronić dane użytkowników. Dane są przetwarzane zgodnie z obowiązującymi przepisami o ochronie danych osobowych.",
      ],
      [
        "Czy moje dokumenty będą widoczne dla wszystkich?",
        "Nie. Dokumenty przekazane w ramach weryfikacji nie powinny być publicznie dostępne. Użytkownikom prezentowany jest przede wszystkim wynik odpowiedniego procesu weryfikacyjnego.",
      ],
      [
        "Czy osoba szukająca opiekunki zobaczy mój adres lub numer dokumentu?",
        "Nie powinno to być konieczne. Portal powinien ograniczać publicznie prezentowane dane do informacji potrzebnych do wyboru opiekunki.",
      ],
      [
        "Czy mogę usunąć swoje konto?",
        "Tak, z zastrzeżeniem sytuacji, w których określone dane muszą być przechowywane przez Operatora przez czas wymagany prawem.",
      ],
    ],
  },
  {
    id: "opinie",
    title: "Opinie i zgłoszenia",
    items: [
      [
        "Czy opinie są weryfikowane?",
        "Portal może stosować mechanizmy mające ograniczać publikowanie fałszywych opinii. W przypadku wątpliwości Operator może poprosić autora o dodatkowe informacje dotyczące jego doświadczenia.",
      ],
      [
        "Co zrobić, jeśli ktoś opublikował fałszywą opinię na mój temat?",
        "Możesz zgłosić opinię Operatorowi i przedstawić informacje uzasadniające zgłoszenie. Sprawa może zostać poddana moderacji.",
      ],
      [
        "Jak zgłosić niebezpieczne lub nielegalne zachowanie?",
        "Skorzystaj z funkcji „Zgłoś” dostępnej przy odpowiedniej treści lub skontaktuj się z Operatorem poprzez kanał wskazany w Serwisie.",
      ],
      [
        "Czy portal może usunąć profil opiekunki?",
        "Tak, w przypadkach określonych w Regulaminie, np. w razie podania nieprawdziwych informacji, poważnego naruszenia zasad bezpieczeństwa, oszustwa lub innych istotnych naruszeń.",
      ],
    ],
  },
  {
    id: "wspolpraca",
    title: "Współpraca opiekunki z rodziną",
    items: [
      [
        "Czy twojaopiekunka.pl zatrudnia opiekunki?",
        "Co do zasady nie. Portal umożliwia kontakt pomiędzy osobami oferującymi usługi opiekuńcze a osobami ich poszukującymi. Warunki zatrudnienia lub współpracy ustalają zainteresowane strony, chyba że dla konkretnej usługi określono inny model.",
      ],
      [
        "Czy portal gwarantuje znalezienie opiekunki?",
        "Nie. Portal ma ułatwiać znalezienie odpowiedniej osoby, ale nie może zagwarantować, że każda osoba poszukująca opieki znajdzie odpowiednią Opiekunkę.",
      ],
      [
        "Czy portal gwarantuje wynagrodzenie opiekunki?",
        "Nie. Wysokość wynagrodzenia i pozostałe warunki współpracy ustalają zainteresowane strony, z uwzględnieniem obowiązujących przepisów.",
      ],
      [
        "Czy portal odpowiada za zachowanie opiekunki?",
        "Portal stosuje procedury mające zwiększyć bezpieczeństwo użytkowników, ale nie może zagwarantować przyszłego zachowania żadnej osoby. Za wykonywanie umowy zawartej pomiędzy użytkownikami co do zasady odpowiadają jej strony.",
      ],
    ],
  },
];

export default function FAQ() {
  const renderAnswer = (text) => {
    const paragraphs = text.split(/\n\n+/);

    return paragraphs.map((paragraph, pIndex) => {
      const lines = paragraph.split("\n");

      return (
        <React.Fragment key={pIndex}>
          {pIndex > 0 && (
            <Box component="span" sx={{ display: "block", height: "0.8em" }} />
          )}

          {lines.map((line, lineIndex) => {
            const parts = line.split("**");

            return (
              <React.Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}

                {parts.map((part, index) =>
                  index % 2 === 1 ? <strong key={index}>{part}</strong> : part,
                )}
              </React.Fragment>
            );
          })}
        </React.Fragment>
      );
    });
  };

  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const { userData } = useContext(UserDataContext);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();

    return faqCategories
      .filter((c) => category === "all" || c.id === category)
      .map((c) => ({
        ...c,
        items: c.items.filter(
          ([question, answer]) =>
            !q ||
            question.toLowerCase().includes(q) ||
            answer.toLowerCase().includes(q),
        ),
      }))
      .filter((c) => c.items.length);
  }, [category, search]);

  const count = groups.reduce((n, c) => n + c.items.length, 0);

  return (
    <div
      style={{
        scrollBehavior: "smooth",
        backgroundColor: "#fafafa",
        padding: "0px 0",
      }}
    >
      <VN userData={userData} />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        {/* NAGŁÓWEK I WYSZUKIWARKA */}
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            twojaopiekunka.pl
          </Typography>

          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 800, mb: 2 }}
          >
            Najczęściej zadawane pytania
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto", mb: 4 }}
          >
            Wszystko, co warto wiedzieć o wyszukiwaniu opiekunki, weryfikacji,
            bezpieczeństwie i korzystaniu z portalu.
          </Typography>

          <Box sx={{ maxWidth: 500, mx: "auto" }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Np. weryfikacja, kontrola, opinie..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setExpanded(false);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearch("")}
                      aria-label="Wyczyść wyszukiwanie"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "16px",
                  backgroundColor: "background.paper",
                },
              }}
            />
            {search && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1 }}
              >
                Znaleziono odpowiedzi: {count}
              </Typography>
            )}
          </Box>
        </Box>

        {/* KATEGORIE (CHIPS) */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap", // <--- Zawija elementy do nowej linii
            gap: 1, // Odstępy między elementami
            justifyContent: "center", // Środkowanie elementów
            mb: 4,
          }}
        >
          <Chip
            label="Wszystkie"
            clickable
            color={category === "all" ? "primary" : "default"}
            variant={category === "all" ? "filled" : "outlined"}
            onClick={() => {
              setCategory("all");
              setExpanded(false);
            }}
            sx={{ fontWeight: 600 }}
          />
          {faqCategories.map((c) => (
            <Chip
              key={c.id}
              label={c.title}
              clickable
              color={category === c.id ? "primary" : "default"}
              variant={category === c.id ? "filled" : "outlined"}
              onClick={() => {
                setCategory(c.id);
                setExpanded(false);
              }}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>

        {/* TREŚĆ FAQ */}
        <Box sx={{ mb: 6 }}>
          {groups.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 5,
                textAlign: "center",
                backgroundColor: "action.hover",
                borderRadius: 4,
              }}
            >
              <HelpOutlineIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Nie znaleziono odpowiedzi
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Spróbuj wpisać inne słowo kluczowe lub wybierz inną kategorię.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearch("");
                  setCategory("all");
                }}
              >
                Pokaż wszystkie pytania
              </Button>
            </Paper>
          ) : (
            groups.map((c) => (
              <Box key={c.id} sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{ fontWeight: 700, mb: 2, px: 1, color: "primary.main" }}
                >
                  {c.title}
                </Typography>

                {c.items.map(([question, answer], index) => {
                  const id = `${c.id}-${index}`;
                  return (
                    <Accordion
                      key={id}
                      expanded={expanded === id}
                      onChange={handleAccordionChange(id)}
                      elevation={0}
                      sx={{
                        mb: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "12px !important",
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, color: "text.secondary" }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          {renderAnswer(answer)}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ))
          )}
        </Box>

        {/* SEKCJA KONTAKTOWA */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 0,
            backgroundColor: "primary.50",
            border: "1px solid",
            borderColor: "primary.100",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 3,
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="primary.main"
              sx={{ fontWeight: 700 }}
            >
              Nie znalazłeś odpowiedzi?
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
              Skontaktuj się z nami
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Masz pytanie dotyczące portalu, weryfikacji lub swojego konta?
              Napisz do nas.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<EmailIcon />}
            href="mailto:kontakt@twojaopiekunka.pl"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            Napisz do nas
          </Button>
        </Paper>

        <Divider sx={{ mb: 3 }} />

        {/* STOPKA INFORMACYJNA */}
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          component="p"
        >
          FAQ ma charakter informacyjny. Szczegółowe zasady korzystania z
          portalu określają Regulamin Serwisu oraz Regulamin Weryfikacji i
          Kontroli Opiekunek.
        </Typography>
      </Container>
    </div>
  );
}
